import { Divider, IconButton, OutlinedInput } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import IcBaselineAddPhotoAlternate from '~icons/ic/baseline-add-photo-alternate'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcBaselinePersonAddAlt from '~icons/ic/baseline-person-add-alt'
import IcBaselinePlaylistAdd from '~icons/ic/baseline-playlist-add'
import UploadImage from 'src/components/Common/UploadImage'
import { LoadingIcon } from 'src/components/LoadingPage'
import NotLogin from 'src/components/NotLogin'
import {
  getModelShareLink,
  ItemShareLink,
} from 'src/components/SocialShare/SocialShare'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { useMyInfo } from 'src/services/apollo/auth'
import type { Media } from 'src/services/apollo/generated/graphql'
import { RefType, ReportReason } from 'src/services/apollo/generated/graphql'
import { executeReport } from 'src/services/apollo/user'
import useLoadingCallback from 'src/utils/useLoadingCallback'

interface ClaimReportExtra {
  statement: string
  mediaIds: string[] // Attachments
}

function ClaimDialog(props: { modelId: string; onComplete?: () => void }) {
  const [statement, setStatement] = useState('')
  const { modelId, onComplete } = props
  const { t } = useTranslation(['market'])
  const [media, setMedia] = useState<Media[]>([])
  const [uploading, setUploading] = useState(false)
  const { data: userInfo, loading } = useMyInfo()

  const [submitting, submit] = useLoadingCallback(
    async (stmt: string) => {
      try {
        await executeReport({
          refId: modelId,
          refType: RefType.Model,
          reason: ReportReason.ClaimModel,
          extra: {
            mediaIds: media.map(m => m.id),
            statement: stmt,
          } as ClaimReportExtra,
        })
        toast.success(t('market:model.claim.notice.submit-success'))
        onComplete?.()
      } catch (err: any) {
        toast.error(
          t('market:model.claim.notice.submit-failed') + ', ' + err.message,
        )
      }
    },
    [media, modelId],
  )

  if (!userInfo) {
    if (loading) return <LoadingIcon />

    return <NotLogin />
  }

  const onMediaChange = (id?: string, newMedia?: Media) => {
    if (!id && newMedia) {
      return setMedia(prev => [...prev, newMedia])
    }
    return setMedia(prev => {
      const prevMediaIdx = prev.findIndex(m => m.id === id)
      const newRes = [...prev]
      newMedia
        ? newRes.splice(prevMediaIdx, 1, newMedia)
        : newRes.splice(prevMediaIdx, 1)
      return newRes
    })
  }

  return (
    <>
      <UIDialogHeader
        className="[&>h2]:flex [&>h2]:gap-2 [&>h2]:items-center"
        heading={
          <>
            <IcBaselinePlaylistAdd className="size-6" />
            {t('market:model.claim.claim-model')}
          </>
        }
      ></UIDialogHeader>
      <UIDialogContent className="pt-2">
        <form
          onSubmit={e => {
            e.preventDefault()
            submit(statement)
          }}
          className="flex flex-col"
          action=""
        >
          <div className="flex gap-2 items-center">
            <div className="inline-flex gap-2 shrink-0">
              {media.map(m => (
                <div key={m.id} className="relative">
                  <UploadImage
                    key={m.id}
                    className="!w-[100px] !h-[100px] !text-xs"
                    value={m}
                    onChange={val => onMediaChange(m.id, val)}
                    onUpload={setUploading}
                  />
                  <IconButton
                    onClick={() => onMediaChange(m.id)}
                    size="small"
                    className="absolute right-0 top-0 translate-x-2 -translate-y-2 bg-white"
                  >
                    <IcBaselineClose className="text-sm" />
                  </IconButton>
                </div>
              ))}
              {media.length < 4 && (
                <UploadImage
                  className="!w-[100px] !h-[100px] !text-xs"
                  value={undefined}
                  onChange={val => onMediaChange(undefined, val)}
                  onUpload={setUploading}
                  placeholderElement={
                    <div>
                      <IcBaselineAddPhotoAlternate className="size-6" />
                    </div>
                  }
                />
              )}
            </div>

            {media.length < 2 && (
              <p className="text-sm shrink-1">
                {t('market:model.claim.claim-description')}
              </p>
            )}
          </div>
          <OutlinedInput
            className="mt-4 w-full"
            multiline
            minRows={3}
            maxRows={6}
            placeholder={t('market:model.claim.claim-placeholder')}
            value={statement}
            onChange={e => setStatement(e.target.value)}
            inputProps={{
              maxLength: 2000,
            }}
            required
          />

          <UIButton
            size="large"
            variant="filled"
            type="submit"
            busy={submitting}
            disabled={uploading}
            className="ml-auto my-4"
          >
            <span>{t('market:model.claim.submit-claim')}</span>
          </UIButton>
        </form>
        <Divider>or</Divider>
        <div className="flex font-semibold text-xl items-center gap-2 mb-2 mt-2">
          <IcBaselinePersonAddAlt />
          <p>{t('market:model.claim.invite-to-claim')}</p>
        </div>
        <ItemShareLink className="w-full" link={getModelShareLink(modelId)} />
      </UIDialogContent>
    </>
  )
}

export const dialogClaim = (modelId: string) => {
  return UIDialog.create({
    dismissable: true,
    children: ({ close }) => (
      <ClaimDialog
        modelId={modelId}
        onComplete={() => {
          close()
        }}
      />
    ),
  })
}
